import { toast } from "sonner";
import { Icon, Tip } from "@/components/ds";
import { api } from "@/lib/api";
import type { ProgramaFuente } from "@/types/capacitaciones-ia";

function descargarBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `descarga_url` ya viene resuelta por el servidor (F6, design #1663 D4) —
 * funciona igual para las dos clases: una fuente `documento_empresa`
 * redirige al endpoint de documentos existente, una `adhoc` la sirve
 * directo. Sólo se ofrece el botón acá para `adhoc`: un documento de
 * empresa conserva la afordancia que ya tiene hoy (ninguna en este bloque),
 * no se le agrega una segunda. */
async function descargarFuente(fuente: ProgramaFuente) {
  try {
    const blob = await api.blob(fuente.descarga_url);
    descargarBlob(blob, fuente.filename_original ?? fuente.etiqueta);
  } catch (e) {
    toast.error(e instanceof Error ? e.message : "No se pudo descargar el archivo.");
  }
}

/**
 * Chips del conjunto ORDENADO de fuentes de un Programa — documento de
 * empresa o archivo ad-hoc, mismo shape para ambas (sdd/nuevo-programa-
 * multifuente, design #1663 Response Contract). Reusado en la fila
 * expandida del listado (`ProgramaFilaExpandida`) y en la pestaña
 * "Capacitaciones" de la Ficha del trabajador (`CoberturaCapacitaciones`).
 *
 * "sólo para este programa" repite la frase que ya usa el drawer "Nuevo
 * programa" para un archivo ad-hoc — es la misma idea (un archivo que no
 * vive en Documentos Empresa) y no hacía falta una segunda forma de decirlo.
 */
export function FuentesChips({ fuentes }: { fuentes: ProgramaFuente[] }) {
  if (fuentes.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
      {fuentes.map((fuente) => {
        const esAdhoc = fuente.tipo === "adhoc";
        return (
          <span
            key={fuente.id}
            className="border-border-subtle bg-card flex min-w-0 max-w-full items-center gap-1.5 rounded-full border py-1 pr-1.5 pl-2.5"
          >
            <Icon
              name={esAdhoc ? "upload_file" : "description"}
              size={14}
              className="text-icon-soft shrink-0"
            />
            <span className="fc-truncate text-[11.5px]">{fuente.etiqueta}</span>
            {esAdhoc && (
              <>
                <span className="text-soft shrink-0 text-[10px]">sólo para este programa</span>
                <Tip texto={`Descargar «${fuente.etiqueta}»`}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      descargarFuente(fuente);
                    }}
                    aria-label={`Descargar «${fuente.etiqueta}»`}
                    className="text-icon-soft hover:bg-primary-50 hover:text-primary-hover shrink-0 rounded-full p-0.5 transition-colors"
                  >
                    <Icon name="download" size={13} />
                  </button>
                </Tip>
              </>
            )}
          </span>
        );
      })}
    </div>
  );
}
